import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { path } from 'ramda';
import ga from 'react-ga';
import moment from 'moment';
import reportException from '../../../shared/lib/report-exception';
import Theme from '../../../src/styles/ThemeManager';
import HeaderComponent from './children/Header/HeaderComponent';
import Footer from './children/Footer/FooterComponent';
import * as StyleConstants from '../../../src/constants/style-constants';
import ToastContainer from '../../../src/containers/Toast/ToastContainer';
import ToastActions from '../../../src/redux/modules/toast/actions';
import defaultContextTypes from '../../../src/components/default-context-types';
import PageBackground from './children/PageBackground';
import componentBase from '../../../src/lib/component-base';
import * as appActions from '../redux/actions';
import * as authActions from '../../Account/redux/actions';
import { subscriptionSuspended } from '../../Account/redux/selectors';
import * as sessionActions from '../../../src/redux/modules/session/actions';
import * as images from '../../../src/constants/images';
import { featureForRoute } from '../../../shared/lib/feature-for-path/index';
import { subscriptionSimpleValueForLevel, subscriptionGAValueForLevel } from '../../../shared/lib/subscription-level-matching/index';
import AccountAttentionDialog from './children/Modals/AccountAttentionDialog';
import RootModal from '../../Modal/components/RootModal';
import ChildRoutes from './routes';

const OUTAGE_PAGE_URL = 'https://static.flightstats.com/v2/outage.html';

const handleGoogletag = o => !Object.keys(o).length && reportException('Googletag object not initialized', {}, 'app-container');

function getCurrentBreakPoint(userAgent) {
  const { breakpoints } = StyleConstants;
  const { isMobile } = userAgent;

  try {
    let name = 'mobile';
    let value = breakpoints[name];
    const windowWidth = window.innerWidth || value;

    if (windowWidth >= breakpoints.desktopSm) {
      name = 'desktopLg';
      value = breakpoints.desktopLg;
    } else if (windowWidth >= breakpoints.tablet) {
      name = 'desktopSm';
      value = breakpoints.desktopSm;
    } else if (windowWidth >= breakpoints.mobile) {
      name = 'tablet';
      value = breakpoints.tablet;
    }

    return {
      name,
      value,
      isMobile,
      userAgent,
    };
  } catch (ex) {
    return {
      name: isMobile ? 'mobile' : 'desktopSm',
      value: isMobile ? breakpoints.mobile : breakpoints.desktopSm,
      isMobile,
      userAgent,
    };
  }
}

// Needed for onTouchTap
// http://stackoverflow.com/a/34015469/988941
// *** 04/2017 ***
// Still need this for material-ui ... until we upgrade that
// TODO: upgrade material ui and rip this out
// injectTapEventPlugin();

const isLoading = s =>
  s.Account.creatingBraintreeSubscription ||
    s.Account.cancellingBraintreeSubscription ||
    s.Account.loading ||
    s.Account.addingFlightAlertsCredits ||
    s.Account.deletingAccount ||
    s.Account.gettingBraintreeClientToken ||
    s.Account.loggingIn ||
    s.Account.sendingEmailVerification ||
    s.Account.signingUp ||
    s.Account.updatingBillingAddress ||
    s.Account.updatingPaymentInformation ||
    s.AirportCurrentConditions.loading ||
    s.AirportDelayMap.loading ||
    s.AirportDeparturesArrivals.loading ||
    s.FlightAlerts.checkFlightsLoading ||
    s.FlightAlerts.currentDateLoading ||
    s.FlightAlerts.deactivateLoading ||
    s.FlightAlerts.deleteFlightAlertLoading ||
    s.FlightAlerts.flightSchedulesLoading ||
    s.FlightAlerts.loading ||
    s.FlightAlerts.newAlertLoading ||
    s.FlightAlerts.reactivateLoading ||
    s.MultiFlightTracker.loading ||
    s.MultiHistoricalFlightStatus.loading ||
    s.SingleFlightTracker.loading ||
    s.SingleHistoricalFlightStatus.loading ||
    (s.DataExports.dataExportQuoteHistoryLoading && !s.DataExports.polling) ||
    s.DataExports.dataExportTypesLoading ||
    s.OnTimePerformance.loading ||
    false;

@connect(
  state => ({
    release: state.App.release,
    routeName: state.App.routeName,
    userAgent: state.App.userAgent,
    deletingAccount: state.Account.deletingAccount,
    deletedAccount: state.Account.deletedAccount,
    user: state.Account.user,
    loggedIn: state.Account.loggedIn,
    isLoading: isLoading(state),
    accountAttentionRequired: state.Account.accountAttentionRequired,
    shouldRedirectToOutagePage: state.Account.shouldRedirectToOutagePage,
  }),
  { ...authActions, ...sessionActions, ...appActions },
)
@componentBase('App')
class App extends Component {
  static propTypes = {
    deletedAccount: PropTypes.bool,
    deletingAccount: PropTypes.bool,
    location: PropTypes.object,
    history: PropTypes.object,
    params: PropTypes.object,
    user: PropTypes.object,
    loggedIn: PropTypes.bool,
    logout: PropTypes.func,
    isLoading: PropTypes.bool,
    setSessionItem: PropTypes.func,
    userAgent: PropTypes.object,
    dismissAccountAttentionDialog: PropTypes.func,
    accountAttentionRequired: PropTypes.bool,
    shouldRedirectToOutagePage: PropTypes.bool,
    getProfile: PropTypes.func,
    displayAccountAttentionDialog: PropTypes.func,
    release: PropTypes.string,
    resetAuthState: PropTypes.func,
    routeName: PropTypes.string,
    routerPush: PropTypes.func,
    setPollingIntervalId: PropTypes.func,
  };

  static childContextTypes = defaultContextTypes;

  constructor(props) {
    super(props);
    const currentBreakPoint = getCurrentBreakPoint(props.userAgent);

    this.state = {
      currentWidth: (currentBreakPoint ? currentBreakPoint.value : null),
      mediaBreakpoint: currentBreakPoint,
    };
    this.polling = null;
    // Will report if there is a logged in user, this is only called on first website load
    this.addUserInfo(props);

    this.redirectedToOutagePage = false;
  }

  getChildContext = () => ({
    history: this.props.history,
    params: this.props.params,
    location: this.props.location,
    mediaBreakpoint: getCurrentBreakPoint(this.props.userAgent),
    muiTheme: Theme,
    images,
    reportButtonPress: this.reportButtonPress,
    reportEvent: this.reportEvent,
    reportPurchase: this.reportPurchase,
    reportUserEvent: this.reportUserEvent,
    reportQueryTimeFrame: this.reportQueryTimeFrame,
    reportSendInteraction: this.reportSendInteraction,
    reportUserSubscribed: this.reportUserSubscribed,
    reportUserUnsubscribed: this.reportUserUnsubscribed,
    reportUserDeleteAccount: this.reportUserDeleteAccount,
    reportUserUpgraded: this.reportUserUpgraded,
    gotoHandler: this.gotoHandler,
    goto: this.goto,
    store: this.context.store,
    currentUser: this.props.user,
    setSessionItem: this.props.setSessionItem,
    toast: { // message, title, sticky, timeoutInMillis
      info: (...args) => this.context.store.dispatch(ToastActions.info(...args)),
      warning: (...args) => this.context.store.dispatch(ToastActions.warning(...args)),
      success: (...args) => this.context.store.dispatch(ToastActions.success(...args)),
      error: (...args) => this.context.store.dispatch(ToastActions.error(...args)),
    },
  });

  componentDidMount = () => {
    window.googletag = window.googletag || {};
    googletag.cmd = googletag.cmd || [];

    handleGoogletag(window.googletag);

    const { deletedAccount, deletingAccount, user } = this.props;
    const shouldPoll = [user,
      path(['email'], user),
      !deletingAccount,
      !deletedAccount,
      !this.polling].every(v => !!v);

    if (shouldPoll) {
      // begin polling the backend for dynamic user info
      this.pollForUserProfile();

      this.polling = true;
    }
  }

  componentWillReceiveProps = (nextProps) => {
    if (nextProps.shouldRedirectToOutagePage) {
      if (this.redirectedToOutagePage) {
        return;
      }

      console.log('redirecting to outage page');
      this.redirectedToOutagePage = true;
      window.location = OUTAGE_PAGE_URL;
      return;
    }
    if (nextProps.deletedAccount) {
      this.handleDeleteAccountSuccess();
    }

    if (nextProps.loggedIn && !this.userReported) {
      // User logged in and not reported yet, add user info to GA
      this.addUserInfo(nextProps);
    } else if (!nextProps.loggedIn && this.userReported) {
      // User logged out
      this.addUserInfo(nextProps);
      this.userReported = false;
    }

    if (nextProps.loggedIn && !this.props.loggedIn) {
      this.pollForUserProfile();
    }

    // Check if this is a protected route
    // and if subscription is suspended
    const protectedFeature = featureForRoute(nextProps.history.location.pathname);
    if (protectedFeature && subscriptionSuspended(this.context.store.getState())) {
      if (!nextProps.accountAttentionRequired) {
        this.props.displayAccountAttentionDialog();
      }
    }
    if (!protectedFeature && nextProps.accountAttentionRequired) {
      this.props.dismissAccountAttentionDialog();
    }
  }

  componentWillUnmount = () => {
    try {
      if (googletag && googletag.apiReady) {
        googletag.destroySlots();
      }
    } catch (err) {
      console.log('unable to destroy google tag slots');
    }
    // HALT POLLING
    this.haltPollingForUserProfile();
  }

  pollForUserProfile = () => {
    const { setPollingIntervalId } = this.props;
    const intervalId = window.setInterval(() => this.props.getProfile(), 60000);
    this.intervalId = intervalId;
    setPollingIntervalId(this.intervalId);
  }

  handleDeleteAccountSuccess = () => {
    const {
      resetAuthState,
      routerPush,
      user,
    } = this.props;
    const {
      toast,
    } = this.context;
    const subscriptionLevel = user && user.subscriptionLevel;
    const reporterLevel = subscriptionLevel || 0;
    resetAuthState();
    this.reportUserDeleteAccount(reporterLevel);
    this.reportButtonPress('Delete Account');
    routerPush('/');
    toast.success('Your account has been deleted.', 'Success');
  }

  haltPollingForUserProfile = () => this.intervalId && window.clearInterval(this.intervalId);

  userReported = false;

  gotoHandler = (url, button) => () => this.goto(url, button);

  goto = (url, button) => {
    this.context.store.dispatch(push(url));

    if (button) {
      this.reportButtonPress(button);
    }
  }

  addUserInfo = (props) => {
    if (props.user != null) { // eslint-disable-line eqeqeq
      this.userReported = true;
      const user = props.user;

      const email = user.email;
      const domain = email.replace(/.*@/, '');
      const userId = user.id;
      const subscription = user.subscriptionActive ? 'true' : 'false';

      let subscriptionLevel = 'Free';
      switch (user.subscriptionLevel) {
        case 1:
          subscriptionLevel = 'Standard';
          break;
        case 3:
          subscriptionLevel = 'Professional';
          break;
        case 11:
          subscriptionLevel = 'Enterprise';
          break;
        default:
          subscriptionLevel = 'Free';
          break;
      }

      const gaUser = {
        userId,
        dimension1: `${userId}`, // dimension1 = userId
        dimension2: domain, // dimension2 = domain
        dimension3: subscription, // dimension3 = Subscription t/f
        dimension4: 'true', // dimension4 = loggedIn t/f
        dimension5: subscriptionLevel, // dimension5 = Subscription level
      };

      ga.set(gaUser);
    } else {
      const gaUser = {
        dimension4: 'false', // dimension1 = loggedIn t/f
      };

      ga.set(gaUser);
    }
  }

  namespacedLabel = label => (this.props.routeName ? `${this.props.routeName}-${label}` : label);

  reportButtonPress = (label, value) => {
    const eventDict = {
      label: this.namespacedLabel(label),
      category: 'User',
      action: 'ButtonPress',
      nonInteraction: false,
    };

    if (value != null) { // eslint-disable-line eqeqeq
      eventDict.value = value;
    }

    ga.event(eventDict);
  }

  reportUserEvent = (action, label, value) => {
    const eventDict = { category: 'User', nonInteraction: false };

    if (action != null) { // eslint-disable-line eqeqeq
      eventDict.action = action;
    }
    if (label != null) { // eslint-disable-line eqeqeq
      eventDict.label = this.namespacedLabel(label);
    }
    if (value != null) { // eslint-disable-line eqeqeq
      eventDict.value = value;
    }

    ga.event(eventDict);
  }

  reportEvent = (category, action, label, value, nonInteraction) => {
    const eventDict = { category, nonInteraction };

    if (action != null) { // eslint-disable-line eqeqeq
      eventDict.action = action;
    }
    if (label != null) { // eslint-disable-line eqeqeq
      eventDict.label = label;
    }
    if (value != null) { // eslint-disable-line eqeqeq
      eventDict.value = value;
    }

    ga.event(eventDict);
  }

  reportPurchase = ({
    id, affiliation, name, revenue,
  }) => {
    const transactionDict = {
      id: '', affiliation: '', revenue: '', shipping: '', tax: '',
    };
    const itemDict = {
      quantity: 1, sku: '', id: '', category: '', name: '', price: '',
    };

    if (id) {
      transactionDict.id = id;
      itemDict.id = id;
    }
    if (affiliation) {
      transactionDict.affiliation = affiliation;
      itemDict.category = affiliation;
    }
    if (name) {
      itemDict.name = name;
    }
    if (revenue) {
      const rev = revenue.toString().replace('$', '');
      transactionDict.revenue = rev;
      itemDict.price = revenue;
    }

    ga.plugin.execute('ecommerce', 'addTransaction', transactionDict);
    ga.plugin.execute('ecommerce', 'addItem', itemDict);
    ga.plugin.execute('ecommerce', 'send');
  }

  reportQueryTimeFrame = (day, month, year) => {
    // On page load, report QueryTimeFrame and Airport
    if (day != null) { // eslint-disable-line eqeqeq
      const diff = -moment().diff(moment().year(year).month(month - 1).date(day), 'days');
      let reportAction = '';

      if (`${diff}` === '0') {
        reportAction = 'Current Day';
      } else if (diff < -7) {
        reportAction = 'Historical - Past 7';
      } else if (diff < -1) {
        reportAction = 'Historical - Recent';
      } else if (`${diff}` === '-1') {
        reportAction = 'Historical - Past Day';
      } else {
        reportAction = 'Future';
      }

      const eventDict = { category: 'QueryTimeFrame', action: reportAction, nonInteraction: true };
      ga.event(eventDict);
    }
  }

  reportSendInteraction = (dict) => {
    ga.send(dict);
  }

  reportUserSubscribed = (level) => {
    ga.set({ dimension3: 'true', dimension5: subscriptionGAValueForLevel(level) });
    ga.event({
      category: 'User', action: 'Subscription', label: `${subscriptionSimpleValueForLevel(level)}Subscribe`, value: level, nonInteraction: true,
    });
  }

  reportUserUpgraded = (level) => {
    ga.set({ dimension3: 'true', dimension5: subscriptionGAValueForLevel(level) });
    ga.event({
      category: 'User', action: 'Subscription', label: `${subscriptionSimpleValueForLevel(level)}Upgrade`, value: level, nonInteraction: true,
    });
  }

  reportUserUnsubscribed = (level) => {
    ga.set({ dimension3: 'false', dimension5: 'Free' });
    ga.event({
      category: 'User', action: 'Subscription', label: `${subscriptionSimpleValueForLevel(level)}Unsubscribe`, value: level, nonInteraction: true,
    });
  }

  reportUserDeleteAccount = (level) => {
    const subValueForLevel = subscriptionSimpleValueForLevel(level);
    ga.set({ dimension3: 'false' });
    ga.event({
      category: 'User', action: 'Subscription', label: `${subValueForLevel}Delete`, value: level || 0, nonInteraction: true,
    });
    if (level > 0) {
      ga.event({
        category: 'User', action: 'Subscription', label: `${subValueForLevel}Unsubscribe`, value: level, nonInteraction: true,
      });
    }
  }

  render = () => {
    const childComponentName = this.props.routeName || '';

    let mainContent = null;
    let isAccountPage = false;
    if (childComponentName === 'Login'
        || childComponentName === 'ForgotPassword'
        || childComponentName === 'SignUp'
        || childComponentName === 'SignUpPreferences'
        || childComponentName === 'VerifyEmail'
        || childComponentName === 'AdditionalInformation'
        || childComponentName === 'SubscriptionOverview') {
      mainContent = (
        <main className={`container ${childComponentName}`} style={{ width: '100%' }}>
          <ChildRoutes />
        </main>
      );
      isAccountPage = true;
    } else {
      mainContent = (
        <main className='container'>
          <ChildRoutes />
        </main>
      );
    }

    const curtainStyle = {
      zIndex: 1501,
      position: 'fixed',
      overflow: 'show',
      margin: 'auto',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    };

    const spinner = (
      <div style={curtainStyle} className='spinner'>
        <div className='rect1' />
        <div className='rect2' />
        <div className='rect3' />
        <div className='rect4' />
        <div className='rect5' />
      </div>
    );

    const delayContent = childComponentName === 'AirportDelayMap' ? 'delay-content' : '';

    return (
      <div>
        {this.props.isLoading ? spinner : null}
        <div className={`content-holder ${delayContent}`} style={{ width: '100%', height: 'auto' }}>
          <PageBackground
            componentName={childComponentName}
            mediaBreakpoint={this.state.mediaBreakpoint}
          />
          <HeaderComponent
            dispatch={this.context.store.dispatch}
            location={this.props.history.location}
            logout={this.props.logout}
            user={this.props.user}
          />
          {mainContent}
          <ToastContainer />
          <RootModal />
          <AccountAttentionDialog
            location={this.props.history.location}
            open={this.props.accountAttentionRequired || false}
          />
          <Footer
            release={this.props.release}
            location={this.props.history.location}
            parsedComponentName={childComponentName}
            isAccountPage={isAccountPage}
            user={this.props.user}
            isDesktop={this.props.userAgent.isDesktop}
          />
        </div>
      </div>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./modules/App/components/container.js